import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  currencyFormat,
  possesiveAdaptationI18n,
} from '../../../services/utils';

import './css/ChangeCoverageScreenPreview.css';
import { LANGUAGES_EN_US } from '../../../i18n';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';

const ChangeCoverageDetails = ({
  annualBenefit = '',
  coverageName = '',
  deductible = 0,
  effectiveDate = '',
  lastDate = '',
  marketingChannelId = MARKETING_CHANNEL_ID.figo,
  petName = '',
  price = 0,
  reimbursement = 0,
}) => {
  const { i18n, t } = useTranslation('changeCoverage');
  const { t: tInsurance } = useTranslation('insurance');

  const annualBenefitWithFormat = useMemo(() => {
    if (!annualBenefit) {
      return '';
    }

    const unlimitedValues = [
      'unlimited', // legacy backend
      tInsurance('unlimited').toLowerCase(), // multiple language support
    ];

    if (unlimitedValues.includes(annualBenefit.toLowerCase())) {
      return annualBenefit;
    }

    // unbelievable
    return `$${annualBenefit.replace('K', ',000')}`;
  }, [annualBenefit, tInsurance]);

  const benefits = useMemo(() => {
    const items = [];

    if (coverageName) {
      items.push({ text: t('coverageName'), value: coverageName });
    }

    items.push({ text: t('annualBenefit'), value: annualBenefitWithFormat });
    items.push({ text: t('reimbursement'), value: reimbursement });
    items.push({ text: t('deductible'), value: deductible });

    return items;
  }, [annualBenefitWithFormat, deductible, reimbursement, coverageName]);

  const priceValue = useMemo(() => {
    if (price) {
      const separator = i18n.language === LANGUAGES_EN_US ? '.' : ',';
      return {
        decimals: price?.split(separator)[1],
        integer: price?.split(separator)[0],
      };
    }

    return { decimals: '', integer: '' };
  }, [price]);

  return (
    <>
      <div className="Change-coverage-screen-price-container">
        <Trans
          components={{
            b: <span className="Change-coverage-screen-price" />,
            s: <span className="Change-coverage-screen-price-decimals" />,
          }}
          i18nKey="changeCoveragePrice"
          ns="insurance"
          values={{
            decimal: priceValue.decimals,
            integer: priceValue.integer,
          }}
        />

        <span className="Change-coverage-screen-price-month">
          {t('preview.pricePerMonth')}
        </span>
      </div>

      <p className="Change-coverage-screen-preview-dates">
        {t('preview.resume1', {
          count: possesiveAdaptationI18n(petName),
          name: petName,
        })}

        <b>{effectiveDate}</b>

        {marketingChannelId === MARKETING_CHANNEL_ID.figo && (
          <>
            {t('preview.resume2')}

            <b> {lastDate}</b>
          </>
        )}

        {t('preview.resume3')}
      </p>

      <h3>{t('preview.coverageDetails')}</h3>

      <div className="Change-coverage-screen-benefits-container">
        {benefits.map((benefit) => {
          const { text, value } = benefit;

          return (
            <div key={text} className="Change-coverage-screen-benefits-column">
              <span className="Change-coverage-screen-benefit-title">
                {text}
              </span>

              <span className="Change-coverage-screen-benefit-value">
                {currencyFormat({ fixed: 0, value })}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

export { ChangeCoverageDetails };
